import { Component, computed, inject } from '@angular/core';
import {
    InstructionsComponent,
    InstructionsWithFieldsContainerComponent,
    FormRowComponent,
} from '@r1-compass-library/components';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgxMatTimepickerComponent, NgxMatTimepickerDirective } from 'ngx-mat-timepicker';
import { AppConstants } from '../../app.constants';
import { DivisionScheduleForm } from '@r1-compass-library/models/division/division-schedule.model';
import { MatButtonModule } from '@angular/material/button';

import { DivisionScheduleFormService } from './division-schedule-form.service';
import { SaveDivisionSettingsFooterComponent } from '../save-footer.component';
import { FormUnsavedChangesWarningDivisionSettings } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { MatDialog } from '@angular/material/dialog';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import {
    GenericConfirmationDialogComponent,
    GenericConfirmationDialogData,
} from '@r1-compass-library/components/dialog/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';

@Component({
    selector: 'r1-compass-app-division-schedule',
    templateUrl: './division-schedule.component.html',
    styleUrl: './division-schedule.component.scss',
    imports: [
        InstructionsComponent,
        ReactiveFormsModule,
        NgxMatTimepickerDirective,
        NgxMatTimepickerComponent,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        SaveDivisionSettingsFooterComponent,
        InstructionsWithFieldsContainerComponent,
        FormRowComponent,
    ],
    providers: [DivisionScheduleFormService],
})
export class DivisionScheduleComponent implements FormUnsavedChangesWarningDivisionSettings {
    protected readonly divisionScheduleFormService = inject(DivisionScheduleFormService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #dialog = inject(MatDialog);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #globalDivisionStore = inject(GlobalDivisionsStore);

    protected days = AppConstants.days;
    protected offsets = AppConstants.utcOffsets;

    protected scheduleFormGroup: FormGroup<DivisionScheduleForm> = this.divisionScheduleFormService.scheduleFormGroup;
    canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.scheduleFormGroup);
    saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningDivisionSettings;

    protected pausedStatusClass = computed(() =>
        this.#globalLoggedInUserStore.selectedDivisionConfig()?.status === 'Paused' ? 'paused' : 'processing'
    );
    protected pausedStatusLabel = computed(() =>
        this.#globalLoggedInUserStore.selectedDivisionConfig()?.status === 'Paused' ? 'Paused' : 'Processing'
    );

    constructor() {
        this.saveGuardHandlerFn({
            form: this.scheduleFormGroup,
            canDeactivate$: this.canDeactivate$,
        });
    }

    public onTogglePause() {
        const isPaused = this.#globalLoggedInUserStore.selectedDivisionConfig()?.status === 'Paused';
        const dialogRef = this.#dialog.open<GenericConfirmationDialogComponent, GenericConfirmationDialogData>(
            GenericConfirmationDialogComponent,
            {
                width: '400px',
                disableClose: true,
                data: {
                    confirmButton: {
                        text: isPaused ? 'Resume' : 'Pause',
                        type: 'stroked',
                    },
                    cancelButton: {
                        text: 'Cancel',
                        type: 'flat',
                        color: 'white',
                        backgroundColor: 'black',
                    },
                    messageContainsHtml: false,
                    message: 'This will affect all plays in this division. Are you sure?',
                },
            }
        );

        dialogRef.afterClosed().subscribe(confirmation => {
            if (confirmation) {
                const statusToSet = isPaused ? 'Enabled' : 'Paused';
                this.divisionScheduleFormService.togglePause(statusToSet);
            }
        });
    }

    public onSave() {
        return this.divisionScheduleFormService.saveDivisionSchedule();
    }
}
